
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import { UserPlus, Pencil, Trash2 } from "lucide-react"
import { UserEditDialog } from "@/components/users/UserEditDialog"
import { UserDeleteDialog } from "@/components/users/UserDeleteDialog"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"

export default function Users() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { data: profiles, isLoading, refetch } = useQuery({
    queryKey: ["profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      return data
    },
  })

  const handleEdit = (userId: string) => {
    setSelectedUserId(userId)
    setEditDialogOpen(true)
  }

  const handleDelete = (userId: string) => {
    setSelectedUserId(userId)
    setDeleteDialogOpen(true)
  }

  const handleInstagramToggle = async (userId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ instagram: !currentValue })
        .eq("id", userId)

      if (error) throw error

      toast({
        title: "Acesso ao Instagram atualizado",
        description: `O acesso ao Instagram foi ${!currentValue ? "concedido" : "revogado"} com sucesso.`,
      })

      refetch()
    } catch (error) {
      console.error("Error updating instagram access:", error)
      toast({
        variant: "destructive",
        title: "Erro ao atualizar acesso",
        description: "Não foi possível atualizar o acesso ao Instagram.",
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
        <Button onClick={() => navigate("/users/new")}>
          <UserPlus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Cidade</TableHead>
              <TableHead>Função</TableHead>
              <TableHead>Instagram</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {profiles?.map((profile) => (
              <TableRow key={profile.id}>
                <TableCell>
                  {profile.first_name} {profile.last_name}
                </TableCell>
                <TableCell>{profile.state || "-"}</TableCell>
                <TableCell>{profile.city || "-"}</TableCell>
                <TableCell>
                  {profile.role === "admin" ? "Administrador" : "Vereador"}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={profile.instagram || false}
                    onCheckedChange={() => handleInstagramToggle(profile.id, profile.instagram || false)}
                  />
                </TableCell>
                <TableCell>
                  {new Date(profile.created_at).toLocaleDateString("pt-BR")}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(profile.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDelete(profile.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <UserEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        userId={selectedUserId}
        onSuccess={refetch}
      />

      <UserDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        userId={selectedUserId}
        onSuccess={refetch}
      />
    </div>
  )
}
